import React, { useEffect, useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import api, { fileUrl, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, LogOut, ExternalLink, Settings as SettingsIcon, Tag, Newspaper } from "lucide-react";
import NewsForm from "../components/NewsForm";
import CategoryManager from "../components/CategoryManager";
import SettingsForm from "../components/SettingsForm";
import { QDMonogram } from "../components/Header";

export default function AdminDashboard() {
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("news");
  const [news, setNews] = useState([]);
  const [editingNews, setEditingNews] = useState(null);
  const [showForm, setShowForm] = useState(false);

  useEffect(() => {
    if (tab === "news") refresh();
  }, [tab]);

  const refresh = async () => {
    try {
      const { data } = await api.get("/admin/news");
      setNews(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not load news");
    }
  };

  if (loading) return <div className="p-12 text-center text-[#76746F]">Loading…</div>;
  if (!user || user.role !== "admin") return <Navigate to="/admin/login" replace />;

  const onDelete = async (id) => {
    if (!window.confirm("Delete this story? This cannot be undone.")) return;
    try {
      await api.delete(`/admin/news/${id}`);
      toast.success("Story deleted");
      refresh();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const onSaved = () => {
    setShowForm(false);
    setEditingNews(null);
    refresh();
  };

  const handleLogout = async () => {
    await logout();
    navigate("/admin/login");
  };

  return (
    <div className="min-h-screen bg-[#FDFBF7] text-[#1A1A1A]">
      {/* Sidebar */}
      <aside className="fixed top-0 left-0 bottom-0 w-60 bg-white border-r border-black/10 px-6 py-8 hidden md:flex flex-col">
        <Link to="/" className="mb-12 flex items-center gap-3">
          <QDMonogram size={36} />
          <span className="font-serif-display text-2xl">Queens <span className="italic font-light">Daily</span></span>
        </Link>
        <nav className="flex-1 space-y-1 font-sans-modern text-sm">
          <NavItem icon={<Newspaper size={16} />} active={tab === "news"} onClick={() => setTab("news")} testId="tab-news">Stories</NavItem>
          <NavItem icon={<Tag size={16} />} active={tab === "categories"} onClick={() => setTab("categories")} testId="tab-categories">Categories</NavItem>
          <NavItem icon={<SettingsIcon size={16} />} active={tab === "settings"} onClick={() => setTab("settings")} testId="tab-settings">Site Settings</NavItem>
        </nav>
        <div className="pt-6 border-t border-black/10 space-y-3 text-xs text-[#76746F]">
          <div>
            <p className="font-medium text-[#1A1A1A]">{user.name}</p>
            <p className="truncate">{user.email}</p>
          </div>
          <Link to="/" className="flex items-center gap-2 hover:text-[#1A1A1A]" data-testid="view-site">
            <ExternalLink size={13} /> View site
          </Link>
          <button onClick={handleLogout} data-testid="logout-button" className="flex items-center gap-2 hover:text-[#1A1A1A]">
            <LogOut size={13} /> Sign out
          </button>
        </div>
      </aside>

      <main className="md:ml-60 px-6 sm:px-10 lg:px-14 py-10">
        <div className="flex items-center justify-between mb-10">
          <div>
            <p className="overline text-[#76746F]">{tab === "news" ? "Editorial" : tab === "categories" ? "Taxonomy" : "Configure"}</p>
            <h1 className="font-serif-display text-4xl sm:text-5xl tracking-tight mt-2">
              {tab === "news" ? "Stories" : tab === "categories" ? "Categories" : "Site Settings"}
            </h1>
          </div>
          {tab === "news" && (
            <button
              data-testid="new-story-button"
              onClick={() => { setEditingNews(null); setShowForm(true); }}
              className="inline-flex items-center gap-2 bg-[#1A1A1A] text-white px-5 py-3 rounded-full hover:bg-[#D4A373] transition-colors text-sm"
            >
              <Plus size={16} /> New story
            </button>
          )}
        </div>

        {/* Mobile tabs */}
        <div className="flex md:hidden gap-2 mb-8">
          {["news","categories","settings"].map(t => (
            <button
              key={t}
              data-testid={`mobile-tab-${t}`}
              onClick={() => setTab(t)}
              className={`px-4 py-2 rounded-full text-xs ${tab === t ? "bg-[#1A1A1A] text-white" : "bg-white border border-black/15 text-[#76746F]"}`}
            >
              {t}
            </button>
          ))}
        </div>

        {tab === "news" && (
          <section data-testid="news-table">
            <div className="bg-white border border-black/10 rounded-xl overflow-hidden">
              <table className="w-full text-left text-sm">
                <thead className="bg-[#F3F1EC] text-[#76746F]">
                  <tr>
                    <th className="px-5 py-4 font-medium">Image</th>
                    <th className="px-5 py-4 font-medium">Title</th>
                    <th className="px-5 py-4 font-medium hidden md:table-cell">Category</th>
                    <th className="px-5 py-4 font-medium hidden lg:table-cell">Editor</th>
                    <th className="px-5 py-4 font-medium hidden md:table-cell">Status</th>
                    <th className="px-5 py-4 font-medium text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {news.length === 0 && (
                    <tr><td colSpan={6} className="px-5 py-12 text-center text-[#76746F]">No stories yet. Click <em>New story</em> to start.</td></tr>
                  )}
                  {news.map((n) => (
                    <tr key={n.id} className="border-t border-black/5">
                      <td className="px-5 py-3">
                        <img src={fileUrl(n.image_url)} alt="" className="w-14 h-14 object-cover rounded-md" />
                      </td>
                      <td className="px-5 py-3 max-w-xs">
                        <p className="font-serif-display text-lg leading-tight">{n.title}</p>
                        <p className="text-xs text-[#76746F] mt-1 line-clamp-1">{n.description}</p>
                      </td>
                      <td className="px-5 py-3 hidden md:table-cell">{n.category}</td>
                      <td className="px-5 py-3 hidden lg:table-cell text-[#76746F]">{n.editor_name}</td>
                      <td className="px-5 py-3 hidden md:table-cell">
                        <span className={`text-xs px-2 py-1 rounded-full ${n.published ? "bg-[#EAE6DF] text-[#1A1A1A]" : "bg-black/5 text-[#76746F]"}`}>
                          {n.published ? "Published" : "Draft"}
                        </span>
                      </td>
                      <td className="px-5 py-3 text-right">
                        <div className="inline-flex items-center gap-2">
                          <button
                            data-testid={`edit-${n.id}`}
                            onClick={() => { setEditingNews(n); setShowForm(true); }}
                            className="w-9 h-9 rounded-full border border-black/15 hover:bg-black hover:text-white transition-colors inline-flex items-center justify-center"
                            aria-label="Edit"
                          >
                            <Pencil size={14} />
                          </button>
                          <button
                            data-testid={`delete-${n.id}`}
                            onClick={() => onDelete(n.id)}
                            className="w-9 h-9 rounded-full border border-black/15 hover:bg-[#B33A3A] hover:text-white hover:border-[#B33A3A] transition-colors inline-flex items-center justify-center"
                            aria-label="Delete"
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        )}

        {tab === "categories" && <CategoryManager />}
        {tab === "settings" && <SettingsForm />}
      </main>

      {showForm && (
        <NewsForm
          existing={editingNews}
          onClose={() => { setShowForm(false); setEditingNews(null); }}
          onSaved={onSaved}
        />
      )}
    </div>
  );
}

function NavItem({ icon, children, active, onClick, testId }) {
  return (
    <button
      data-testid={testId}
      onClick={onClick}
      className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-md transition-colors ${active ? "bg-[#1A1A1A] text-white" : "text-[#76746F] hover:bg-black/5 hover:text-[#1A1A1A]"}`}
    >
      {icon}
      <span>{children}</span>
    </button>
  );
}
